'use client'

import type { FormFieldBlock, Form as FormType } from '@payloadcms/plugin-form-builder/types'

import { useRouter } from 'next/navigation'
import React, { useCallback, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

import { fields } from './fields'
import { getClientSideURL } from '@/utilities/getURL'

export type FormBlockType = {
  blockName?: string
  blockType?: 'formBlock'
  title?: string | null
  subtitle?: string | null
  enableIntro?: boolean | null
  form: FormType
  introContent?: DefaultTypedEditorState
}

export const FormBlockComponent: React.FC<{ id?: string } & FormBlockType> = (props) => {
  const {
    title,
    subtitle,
    form: formFromProps,
    form: { id: formID, confirmationMessage, confirmationType, redirect, submitButtonLabel } = {},
  } = props

  const formMethods = useForm({ defaultValues: formFromProps.fields })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const onSubmit = useCallback(
    (data: FormFieldBlock[]) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      const submitForm = async () => {
        setError(undefined)

        const dataToSend = Object.entries(data).map(([name, value]) => ({ field: name, value }))

        loadingTimerID = setTimeout(() => setIsLoading(true), 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({ form: formID, submissionData: dataToSend }),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          })

          const res = await req.json()

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)
            setError({ message: res.errors?.[0]?.message || 'Une erreur est survenue', status: res.status })
            return
          }

          setIsLoading(false)
          setHasSubmitted(true)

          if (confirmationType === 'redirect' && redirect?.url) {
            router.push(redirect.url)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({ message: 'Une erreur est survenue.' })
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType],
  )

  return (
    <section className="alt" id="contact">
      <div className="section-inner container">
        <div className="section-head centered">
          <p className="eyebrow">Contact</p>
          {title && <h2>{title}</h2>}
          {subtitle && <p className="mt-3 mx-auto">{subtitle}</p>}
        </div>
        <div className="max-w-xl mx-auto">
          <div className="contact-form">
            <FormProvider {...formMethods}>
              {!isLoading && hasSubmitted && confirmationType === 'message' && (
                <p role="status">
                  {typeof confirmationMessage === 'string'
                    ? confirmationMessage
                    : 'Merci, votre message a bien été envoyé !'}
                </p>
              )}
              {isLoading && !hasSubmitted && (
                <p role="status" aria-live="polite">
                  Envoi en cours…
                </p>
              )}
              {error && (
                <p role="alert" style={{ color: '#b3261e' }}>
                  {error.message}
                </p>
              )}
              {!hasSubmitted && (
                <form id={formID} onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                  {formFromProps?.fields?.map((field, index) => {
                    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                    if (!Field) return null
                    return (
                      <Field key={index} {...field} {...formMethods} control={control} errors={errors} register={register} />
                    )
                  })}
                  <button type="submit" className="cta-primary" style={{ alignSelf: 'flex-start' }}>
                    {submitButtonLabel || 'Envoyer'}
                  </button>
                </form>
              )}
            </FormProvider>
          </div>
        </div>
      </div>
    </section>
  )
}
